"use client"

// import './styles.css';
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/zoom"
import "swiper/css/pagination"
import "swiper/css/effect-cards"
import { Box, Heading, Image, Text, VStack } from "@chakra-ui/react"
// import required modules
import { EffectCards, Navigation, Pagination } from "swiper/modules"
import { EffectCreative } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

interface SlideData {
  src: string
  slideTitle: string
  slideDescription: string
}

interface CarouselProps {
  slides: SlideData[]
  padding?: boolean
  bgColor?: string
  showLegend?: boolean
}

const Carousel = ({
  bgColor = "bg.paper",
  padding,
  showLegend,
  slides,
}: CarouselProps) => {
  const EffectCreativeConfig = {
    next: {
      translate: ["100%", 0, 0],
    },
    prev: {
      shadow: true,
      translate: [0, 0, -400],
    },
  }
  const effectCreativeModules = [
    EffectCards,
    Navigation,
    Pagination,
    EffectCreative,
  ]
  const stackModules = [EffectCards, Navigation, Pagination]
  const paginationEffectCreative = {
    el: ".work-carousel-pagination",
    type: "progressbar" as const,
  }

  return (
    <Box
      css={{
        "& .swiper": {
          overflow: padding ? "visible" : "hidden",
        },
        "& .swiper-3d .swiper-slide-shadow": {
          backgroundColor: bgColor || "var(--gil-colors-bg-subtle)",
          border: padding ? "1px solid var(--gil-colors-border)" : "none",
          borderRadius: padding ? "3xl" : "0",
        },
        "& .swiper-button-next, & .swiper-button-prev": {
          color: "var(--gil-colors-fg-subtle)",
          transition: "color 0.1s ease-in-out",
        },
        "& .swiper-button-next:hover, & .swiper-button-prev:hover": {
          color: "var(--gil-colors-primary-solid)",
        },
        "& .swiper-cards .swiper-slide": {
          borderRadius: padding ? "3xl" : "0",
        },
        "& .swiper-horizontal > .swiper-pagination-bullets, .swiper-pagination-bullets.swiper-pagination-horizontal":
          {
            bottom: "0px !important",
            top: "inherit !important",
          },
        "& .swiper-horizontal > .swiper-pagination-progressbar, .swiper-pagination-progressbar.swiper-pagination-horizontal":
          {
            top: "inherit !important",
            zIndex: 10,
          },
        "& .swiper-pagination-bullet": {
          backgroundColor: "var(--gil-colors-primary-solid)",
        },
        "& .swiper-pagination-progressbar": {
          backgroundColor: "var(--gil-colors-border)",
        },
        "& .swiper-pagination-progressbar .swiper-pagination-progressbar-fill":
          {
            backgroundColor: "var(--gil-colors-primary-solid)",
          },
      }}
      data-print-carousel=""
      h="full"
      position="relative"
      w="full"
    >
      <Swiper
        autoHeight={true}
        centeredSlides={true}
        className="work-carousel-swiper"
        creativeEffect={padding ? undefined : EffectCreativeConfig}
        effect={padding ? "cards" : "creative"}
        grabCursor={true}
        modules={padding ? stackModules : effectCreativeModules}
        navigation={true}
        pagination={padding ? undefined : paginationEffectCreative}
        rewind={true}
        slidesPerView={1}
        spaceBetween={20}
        style={{
          height: "100%",
          width: "100%",
        }}
        zoom={{
          maxRatio: 5,
          toggle: true,
        }}
      >
        {slides.map((slide) => (
          <SwiperSlide key={`${slide.src}-${slide.slideTitle}`}>
            <Box
              alignItems="center"
              bgColor={bgColor || "bg.paper"}
              border={padding ? "1px solid var(--gil-colors-border)" : "none"}
              borderRadius={padding ? "3xl" : "0"}
              boxShadow="md"
              className="slide"
              color="white"
              cursor="pointer"
              data-print-carousel-slide=""
              display="flex"
              flexDirection="column"
              h="full"
              justifyContent="center"
              overflow="hidden"
              position="relative"
              textAlign="center"
              w="full"
            >
              <Image
                alt={slide.slideTitle}
                aria-label={slide.slideTitle}
                decoding="sync"
                h="auto"
                loading="eager"
                maxH="70vh"
                objectFit="contain"
                position="relative"
                src={slide.src}
                transition="opacity 0.6s ease-in-out"
                w="full"
              />
              {showLegend && (
                <VStack
                  alignItems={padding ? "flex-start" : "center"}
                  animation="slide-in-bottom 1s ease-in-out"
                  bgColor={bgColor || "bg.paper"}
                  justifyContent={padding ? "flex-start" : "center"}
                  position="relative"
                  px={{
                    base: padding ? 10 : 6,
                  }}
                  py={{
                    base: 4,
                    md: 6,
                    lg: 6,
                  }}
                  transition="all 1s"
                  width="full"
                >
                  <Heading
                    as="h3"
                    color="primary.fg"
                    fontSize="md"
                    fontWeight="semibold"
                    lineHeight="tight"
                    size="lg"
                    textAlign={padding ? "left" : "center"}
                    w="full"
                  >
                    {slide.slideTitle}
                  </Heading>
                  <Text
                    color="fg.muted"
                    fontFamily="mono"
                    fontSize="sm"
                    lineClamp={2}
                    lineHeight="tight"
                    textAlign={padding ? "left" : "center"}
                    w="full"
                  >
                    {slide.slideDescription}
                  </Text>
                </VStack>
              )}
            </Box>
          </SwiperSlide>
        ))}
        <Box
          background="rgba(0,0,0,0.1)"
          bottom={0}
          className="work-carousel-pagination"
          display={padding ? "none" : "block"}
          height="4px"
          left={0}
          position="absolute"
          top={"inherit"}
          width="100%"
        />
      </Swiper>
    </Box>
  )
}

export default Carousel
